import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';

interface StreakData {
  current_streak: number;
  longest_streak: number;
  total_logins: number;
  last_login_date: string | null;
}

export default function LoginStreakBanner() {
  const { supabaseUser, isRegistered } = useAuth();
  const [streak, setStreak] = useState<StreakData | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isNewDay, setIsNewDay] = useState(false);

  useEffect(() => {
    if (!isRegistered || !supabaseUser) return;
    updateStreak();
  }, [isRegistered, supabaseUser]);

  const updateStreak = async () => {
    if (!supabaseUser) return;

    const today = new Date().toISOString().split('T')[0];

    const { data: existing } = await supabase
      .from('login_streaks')
      .select('*')
      .eq('user_id', supabaseUser.id)
      .maybeSingle();

    if (!existing) {
      // Primera vez
      const newStreak: Omit<StreakData, 'id'> & { user_id: string; last_login_date: string } = {
        current_streak: 1,
        longest_streak: 1,
        total_logins: 1,
        last_login_date: today,
      };
      const { data } = await supabase
        .from('login_streaks')
        .insert({ user_id: supabaseUser.id, ...newStreak })
        .select()
        .maybeSingle();
      if (data) {
        setStreak(data as StreakData);
        setIsNewDay(true);
        setShowBanner(true);
      }
      return;
    }

    const lastLogin = existing.last_login_date;

    if (lastLogin === today) {
      // Ya se registró hoy, solo mostrar si quiere
      setStreak(existing as StreakData);
      return;
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    let newStreak = 1;
    if (lastLogin === yesterdayStr) {
      newStreak = existing.current_streak + 1;
    }

    const newLongest = Math.max(existing.longest_streak, newStreak);

    const { data: updated } = await supabase
      .from('login_streaks')
      .update({
        current_streak: newStreak,
        longest_streak: newLongest,
        total_logins: existing.total_logins + 1,
        last_login_date: today,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', supabaseUser.id)
      .select()
      .maybeSingle();

    if (updated) {
      setStreak(updated as StreakData);
      setIsNewDay(true);
      setShowBanner(true);
    }
  };

  if (!showBanner || !streak) return null;

  const milestones = [3, 7, 14, 30, 60, 100];
  const nextMilestone = milestones.find((m) => m > streak.current_streak) ?? 100;
  const progress = Math.min((streak.current_streak / nextMilestone) * 100, 100);

  const getStreakEmoji = (s: number) => {
    if (s >= 30) return 'ri-fire-fill';
    if (s >= 14) return 'ri-flashlight-fill';
    if (s >= 7) return 'ri-star-fill';
    if (s >= 3) return 'ri-award-fill';
    return 'ri-calendar-check-line';
  };

  const getStreakColor = (s: number) => {
    if (s >= 30) return '#ff6b35';
    if (s >= 14) return '#ffd700';
    if (s >= 7) return '#00e87a';
    return '#a8b2c1';
  };

  const color = getStreakColor(streak.current_streak);

  return (
    <div
      className="fixed bottom-6 right-6 z-40 max-w-sm w-full animate-[slideUp_0.4s_ease-out]"
      style={{ animation: 'slideUp 0.4s ease-out' }}
    >
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      <div
        className="bg-[#151719] border rounded-2xl p-5 shadow-2xl"
        style={{ borderColor: `${color}40` }}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 flex items-center justify-center rounded-xl"
              style={{ background: `${color}20` }}
            >
              <i className={`${getStreakEmoji(streak.current_streak)} text-xl`} style={{ color }}></i>
            </div>
            <div>
              <p className="text-white font-black text-base leading-tight">
                {isNewDay && streak.current_streak > 1 ? '¡Racha mantenida!' : streak.current_streak === 1 ? '¡Bienvenido!' : '¡Racha activa!'}
              </p>
              <p className="text-[#6b7280] text-xs">
                {streak.total_logins} accesos totales
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowBanner(false)}
            className="w-6 h-6 flex items-center justify-center text-[#6b7280] hover:text-white rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>

        <div className="flex items-center gap-4 mb-4">
          <div className="text-center">
            <p className="font-black text-3xl" style={{ color }}>{streak.current_streak}</p>
            <p className="text-[#6b7280] text-xs">días seguidos</p>
          </div>
          <div className="flex-1 space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-[#6b7280]">Próximo hito: {nextMilestone} días</span>
              <span style={{ color }}>{streak.current_streak}/{nextMilestone}</span>
            </div>
            <div className="h-2 bg-[#2a2d3a] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${progress}%`, background: color }}
              ></div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 bg-[#0d0f14] rounded-xl p-3 text-center">
            <p className="text-white font-bold text-sm">{streak.longest_streak}</p>
            <p className="text-[#6b7280] text-xs">Mejor racha</p>
          </div>
          <div className="flex-1 bg-[#0d0f14] rounded-xl p-3 text-center">
            <p className="text-white font-bold text-sm">{streak.total_logins}</p>
            <p className="text-[#6b7280] text-xs">Total accesos</p>
          </div>
          {streak.current_streak >= 7 && (
            <div className="flex-1 bg-[#0d0f14] rounded-xl p-3 text-center">
              <p className="font-bold text-sm" style={{ color }}>
                {streak.current_streak >= 30 ? '🔥 Pro' : streak.current_streak >= 14 ? '⚡ Activo' : '⭐ Regular'}
              </p>
              <p className="text-[#6b7280] text-xs">Nivel</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
